import { NgClass } from '@angular/common'
import { Component, Signal, computed, effect, inject } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { Router } from '@angular/router'

import { Store } from '@ngrx/store'
import { TooltipModule } from 'primeng/tooltip'

import { filter, map, switchMap } from 'rxjs'

import { FlxButtonModule, FlxFormControlModule, FlxIconModule } from '@flowx/angular-ui-toolkit'
import { AiBuilderToggleComponent } from '@flowx/designer/modules/ai-builder/components/ai-builder-toggle/ai-builder-toggle.component'
import { CommentModeToggleComponent } from '@flowx/designer/modules/ai-builder/components/comment-mode/comment-mode-toggle/comment-mode-toggle.component'
import { AiBuilderStore } from '@flowx/designer/modules/ai-builder/store/ai-builder.store'
import { TOOLTIP_DEFAULT_DELAY } from '@flowx/designer/modules/core/dictionary/constants'
import { RunDesignerLinkStore } from '@flowx/designer/modules/layout/store/run-designer-link.store'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import {
  NavigationTabs,
  selectedNavigationTab,
} from '@flowx/designer/modules/projects/utils/navigation-tabs.util'
import { FlxThemesLoaderService } from '@flowx/designer/modules/theme/services/themes-loader.service'
import { ApplicationType } from '@flowx/designer/shared/entities/application.entity'
import { GuardCoordinatorService } from '@flowx/designer/shared/services/guard-coordinator.service'
import { FlxNavigationService } from '@flowx/designer/shared/services/navigation.service'
import {
  selectAppId,
  selectAppVersionId,
  selectStableMergedRouteData,
  selectUrlDataParams,
  selectWorkspaceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'
import { LayoutOptions } from '@flowx/designer/shared/types/layout.types'

import { ProjectLaunchService } from './project-launch.service'

/** The icon before the project's name, and its tooltip. */
const APPLICATION_TYPE_ICONS: Record<ApplicationType, { iconClass: string; tooltip: string }> = {
  [ApplicationType.APP]: { iconClass: 'ph-folder', tooltip: 'Project' },
  [ApplicationType.LIB]: { iconClass: 'ph-books', tooltip: 'Library' },
}

/**
 * The project's part of the global header: its name, the Config | Runtime toggle, the AI entry,
 * comment mode and Run.
 *
 * The global header creates it with the project layout's injectors (`layoutComponentOutlet`), so it
 * reads the layout's stores and route.
 */
@Component({
  selector: 'flx-project-header',
  imports: [
    NgClass,
    ReactiveFormsModule,
    TooltipModule,
    FlxButtonModule,
    FlxFormControlModule,
    FlxIconModule,
    AiBuilderToggleComponent,
    CommentModeToggleComponent,
  ],
  providers: [ProjectLaunchService, FlxThemesLoaderService],
  templateUrl: './project-header.component.html',
  styleUrl: './project-header.component.scss',
  host: { class: 'flex flex-1 items-center gap-x-3 min-w-0' },
})
export class FlxProjectHeaderComponent {
  #router = inject(Router)
  #store = inject(Store)
  #navigationService = inject(FlxNavigationService)
  #guardCoordinator = inject(GuardCoordinatorService)
  #aiBuilder = inject(AiBuilderStore)
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #projectLaunchService = inject(ProjectLaunchService)
  #runDesignerLink = inject(RunDesignerLinkStore)

  #workspaceId = this.#store.selectSignal(selectWorkspaceId)
  #appId = this.#store.selectSignal(selectAppId)
  #appVersionId = this.#store.selectSignal(selectAppVersionId)

  protected TOOLTIP_DEFAULT_DELAY = TOOLTIP_DEFAULT_DELAY
  NavigationTabs = NavigationTabs

  appName = this.#applicationDetailsStore.appName
  appType = this.#applicationDetailsStore.appType
  applicationVersion = this.#applicationDetailsStore.applicationVersion
  applicationDefinition = this.#applicationDetailsStore.applicationDefinition

  appTypeIcon = computed(() => APPLICATION_TYPE_ICONS[this.appType()])

  routeData = this.#store.selectSignal(selectStableMergedRouteData)

  /**
   * The tab of the route a navigation commits to: Config under the version route and on a running
   * instance, Runtime anywhere else in the project. A switch the unsaved-changes guard cancels
   * leaves it, and the version block, as they were.
   */
  activeTab = computed(() => selectedNavigationTab(this.routeData()))

  tabs: Signal<
    {
      value: string
      label: string
      disabled: boolean
    }[]
  > = computed(() => {
    const editAppVersion = this.applicationDefinition()?.editAppVersion
    const applicationVersion = this.applicationVersion()
    return [
      {
        value: NavigationTabs.CONFIG,
        label: 'Config',
        disabled: !(editAppVersion || applicationVersion),
      },
      { value: NavigationTabs.RUNTIME, label: 'Runtime', disabled: false },
    ]
  })

  /**
   * Whether Run is offered here, not whether an entry point is set: that is checked on click.
   * Libraries have no runtime, and a running instance, which has the header only while the AI
   * assistant is open, hosts a live SDK session that the runtime launch would overwrite.
   */
  showLaunch = computed(
    () =>
      this.appType() === ApplicationType.APP &&
      !this.routeData()?.[LayoutOptions.IS_RUNNING_INSTANCE]
  )

  /** The toggle moves on click, ahead of the navigation, and returns to `activeTab` if it's cancelled. */
  tabControl = new FormControl<string>(this.activeTab())

  previousConfigRouteData = toSignal(
    this.#store
      .select(selectUrlDataParams)
      .pipe(filter((urlDataParams) => urlDataParams.data?.routeKey === 'config'))
  )

  constructor() {
    // The route also changes without the toggle: a link, history, a version switch.
    effect(() => this.tabControl.setValue(this.activeTab(), { emitEvent: false }))

    this.tabControl.valueChanges
      .pipe(
        // Compared against the route, not the last emission: a cancel restores the toggle with
        // emitEvent: false, so distinctUntilChanged would still hold the abandoned tab and swallow
        // the retry.
        filter((value) => value !== this.activeTab()),
        // The arrow keys select a disabled tab too: the toolkit re-enables every option when the
        // form control sets its disabled state. Put the toggle back and stay.
        filter((value) => {
          const tabDisabled = this.tabs().find((tab) => tab.value === value)?.disabled

          if (tabDisabled) {
            this.tabControl.setValue(this.activeTab(), { emitEvent: false })
          }

          return !tabDisabled
        }),
        switchMap((value) =>
          this.#guardCoordinator.confirmProceed().pipe(map((canProceed) => ({ value, canProceed })))
        ),
        takeUntilDestroyed()
      )
      .subscribe(({ value, canProceed }) => {
        if (!canProceed) {
          this.tabControl.setValue(this.activeTab(), { emitEvent: false })
          return
        }

        // The plan is transient over the designer: picking a section is also a way home from it.
        this.#aiBuilder.backToMake()

        if (value === NavigationTabs.CONFIG) {
          // Config after a run goes back to the designer of what was run, not to wherever the
          // maker last was in config.
          const runDesignerLink = this.#runDesignerLink.take(this.#appId())
          if (runDesignerLink) {
            this.#router.navigate([...runDesignerLink])
            return
          }

          const appVersionId =
            this.#appVersionId() ?? this.applicationDefinition()?.editAppVersion?.appVersionId
          if (appVersionId) {
            // The last config page is reopened only for the project on screen: the header outlives a
            // jump straight into another project (a build import notification's View, say), and
            // returning to it would reopen the project left behind. An appId is unique per workspace
            // only, so the workspace has to match as well.
            const previousConfigParams = this.previousConfigRouteData()?.params

            if (
              previousConfigParams?.workspaceId === this.#workspaceId() &&
              previousConfigParams?.appId === this.#appId()
            ) {
              this.#router.navigateByUrl(this.previousConfigRouteData().url)
            } else {
              this.#navigationService.navigateToApplicationVersion(appVersionId)
            }
          }
        }

        if (value === NavigationTabs.RUNTIME) {
          this.#navigationService.navigateToApplicationRuntime()
        }
      })
  }

  launchEntryPoint(): void {
    if (this.activeTab() === NavigationTabs.CONFIG) {
      this.#projectLaunchService.launchVersion()
    } else {
      this.#projectLaunchService.launchRuntime()
    }
  }

  /** The project's default page on the active tab. */
  navigateToProjectHome(): void {
    if (this.activeTab() === NavigationTabs.CONFIG) {
      this.#navigationService.navigateToApplicationVersion()
    } else {
      this.#navigationService.navigateToApplicationRuntime()
    }
  }
}
